/**
 * Plant Loop Detector
 *
 * Detects and reconstructs plant loops from parsed IDF documents.
 * Analyzes PlantLoop objects and related branches to build visual graphs.
 */

import type { IDFObject } from '../../parser';
import type { PlantLoopType, PlantLoopSide } from '../types';
import type {
  DetectedPlantComponent,
  DetectedBranch,
  DetectedPlantLoop,
  PlantNodeConnection,
  PlantDetectionResult,
} from './types';
import { getPlantMappingByIDFClass, isPlantClass } from '../sync/fieldMappings';
import { getPlantComponentDefinition } from '../components';

/**
 * EnergyPlus field indices for plant loop objects
 */
const FIELD_INDICES = {
  PlantLoop: {
    name: 0,
    fluidType: 1,
    userDefinedFluidType: 2,
    plantEquipmentOperationSchemeName: 3,
    loopTempSetpointNodeName: 4,
    maxLoopTemp: 5,
    minLoopTemp: 6,
    maxLoopFlowRate: 7,
    minLoopFlowRate: 8,
    loopVolume: 9,
    supplyInletNode: 10,
    supplyOutletNode: 11,
    supplyBranchListName: 12,
    supplyConnectorListName: 13,
    demandInletNode: 14,
    demandOutletNode: 15,
    demandBranchListName: 16,
    demandConnectorListName: 17,
    loadDistribution: 18,
  },
  Branch: {
    name: 0,
    // Component fields start at index 1 in groups of 4
  },
  BranchList: {
    name: 0,
    // Branch names start at index 1
  },
  ConnectorList: {
    name: 0,
    // Connector type and name pairs start at index 1
  },
  'Connector:Splitter': {
    name: 0,
    inletBranchName: 1,
    // Outlet branch names start at index 2
  },
  'Connector:Mixer': {
    name: 0,
    outletBranchName: 1,
    // Inlet branch names start at index 2
  },
};

/**
 * Get field value from IDF object by index
 */
function getField(obj: IDFObject, index: number): string {
  return obj.fields[index]?.value ?? '';
}

/**
 * Determine loop type from loop name and equipment
 */
function inferLoopType(
  loopName: string,
  fluidType: string,
  components: DetectedPlantComponent[]
): PlantLoopType {
  const nameLower = loopName.toLowerCase();

  // Check for explicit naming
  if (nameLower.includes('chilled') || nameLower.includes('chw')) {
    return 'chilled-water';
  }
  if (nameLower.includes('condenser') || nameLower.includes('cw loop')) {
    return 'condenser-water';
  }
  if (nameLower.includes('hot water') || nameLower.includes('hw') || nameLower.includes('heating')) {
    return 'hot-water';
  }
  if (nameLower.includes('ground') || nameLower.includes('geo')) {
    return 'ground-loop';
  }
  if (nameLower.includes('service') || nameLower.includes('domestic') || nameLower.includes('dhw')) {
    return 'service-hot-water';
  }

  // Check equipment types
  for (const comp of components) {
    if (comp.idfClass.includes('Chiller')) return 'chilled-water';
    if (comp.idfClass.includes('Boiler')) return 'hot-water';
    if (comp.idfClass.includes('CoolingTower') || comp.idfClass.includes('FluidCooler')) return 'condenser-water';
    if (comp.idfClass.includes('GroundHeatExchanger')) return 'ground-loop';
    if (comp.idfClass.includes('WaterHeater')) return 'service-hot-water';
  }

  // Check fluid type for glycol (likely ground loop)
  if (fluidType.toLowerCase().includes('glycol')) {
    return 'ground-loop';
  }

  // Default to chilled water
  return 'chilled-water';
}

/**
 * Extract component from IDF object for plant systems
 */
function extractPlantComponent(obj: IDFObject): DetectedPlantComponent | null {
  if (!isPlantClass(obj.className)) {
    return null;
  }

  const mapping = getPlantMappingByIDFClass(obj.className);
  if (!mapping) {
    return null;
  }

  const definition = getPlantComponentDefinition(mapping.componentType);
  if (!definition) {
    return null;
  }

  // Get component name
  const name = getField(obj, mapping.nameFieldIndex);

  // Extract field values
  const fields: Record<string, string> = {};
  for (const [fieldName, fieldMapping] of Object.entries(mapping.fieldMappings)) {
    const value = getField(obj, fieldMapping.fieldIndex);
    if (value) {
      fields[fieldName] = value;
    }
  }

  // Extract node connections
  const nodeConnections = new Map<string, string>();
  const inletNodes: string[] = [];
  const outletNodes: string[] = [];

  for (const portMapping of mapping.portMappings) {
    const nodeName = getField(obj, portMapping.fieldIndex);
    if (nodeName) {
      nodeConnections.set(nodeName, portMapping.portId);

      // Categorize by port direction
      const port = definition.ports.find(p => p.id === portMapping.portId);
      if (port) {
        if (port.direction === 'inlet') {
          inletNodes.push(nodeName);
        } else {
          outletNodes.push(nodeName);
        }
      }
    }
  }

  return {
    name,
    idfClass: obj.className,
    componentType: mapping.componentType,
    idfObjectId: obj.id,
    fields,
    nodeConnections,
    inletNodes,
    outletNodes,
  };
}

/**
 * Parse Branch object to extract components
 */
function parsePlantBranch(
  branchObj: IDFObject,
  allObjects: IDFObject[],
  loopSide: PlantLoopSide
): DetectedBranch {
  const name = getField(branchObj, 0);
  const components: DetectedPlantComponent[] = [];
  let inletNode = '';
  let outletNode = '';

  const fields = branchObj.fields;

  // Branch fields:
  // Field 0: Name
  // Field 1: Pressure Drop Curve Name
  // Fields 2+ are in groups of 4: Component Type, Name, Inlet Node, Outlet Node
  let fieldIndex = 2;
  let firstInlet = true;

  while (fieldIndex + 3 < fields.length) {
    const componentType = fields[fieldIndex]?.value ?? '';
    const componentName = fields[fieldIndex + 1]?.value ?? '';
    const componentInletNode = fields[fieldIndex + 2]?.value ?? '';
    const componentOutletNode = fields[fieldIndex + 3]?.value ?? '';

    if (!componentType || !componentName) {
      fieldIndex += 4;
      continue;
    }

    // Track branch inlet/outlet
    if (firstInlet && componentInletNode) {
      inletNode = componentInletNode;
      firstInlet = false;
    }
    if (componentOutletNode) {
      outletNode = componentOutletNode;
    }

    // Find the actual component object
    const componentObj = allObjects.find(
      obj => obj.className === componentType && getField(obj, 0) === componentName
    );

    if (componentObj) {
      const detected = extractPlantComponent(componentObj);
      if (detected) {
        detected.loopSide = loopSide;
        detected.branchName = name;

        // Get the component definition to find actual port IDs
        const compDef = getPlantComponentDefinition(detected.componentType);

        // Find the first inlet and outlet port IDs from the component definition
        const inletPortId = compDef?.ports.find(p => p.direction === 'inlet')?.id ?? 'water-inlet';
        const outletPortId = compDef?.ports.find(p => p.direction === 'outlet')?.id ?? 'water-outlet';

        // Add branch-specified nodes to nodeConnections Map
        // This ensures buildPlantNodeConnections can find them
        if (componentInletNode && !detected.inletNodes.includes(componentInletNode)) {
          detected.inletNodes.push(componentInletNode);
          if (!detected.nodeConnections.has(componentInletNode)) {
            detected.nodeConnections.set(componentInletNode, inletPortId);
          }
        }
        if (componentOutletNode && !detected.outletNodes.includes(componentOutletNode)) {
          detected.outletNodes.push(componentOutletNode);
          if (!detected.nodeConnections.has(componentOutletNode)) {
            detected.nodeConnections.set(componentOutletNode, outletPortId);
          }
        }

        components.push(detected);
      }
    }

    fieldIndex += 4;
  }

  return {
    name,
    idfObjectId: branchObj.id,
    inletNode,
    outletNode,
    components,
  };
}

/**
 * Parse BranchList to get branch names
 */
function getBranchNames(branchListName: string, allObjects: IDFObject[]): string[] {
  const branchList = allObjects.find(
    obj => obj.className === 'BranchList' && getField(obj, 0) === branchListName
  );

  if (!branchList) return [];

  const branchNames: string[] = [];
  for (let i = 1; i < branchList.fields.length; i++) {
    const branchName = branchList.fields[i]?.value;
    if (branchName) {
      branchNames.push(branchName);
    }
  }

  return branchNames;
}

/**
 * Parse ConnectorList to get splitter and mixer names
 */
function getConnectors(connectorListName: string, allObjects: IDFObject[]): {
  splitter?: string | undefined;
  mixer?: string | undefined;
} {
  const connectorList = allObjects.find(
    obj => obj.className === 'ConnectorList' && getField(obj, 0) === connectorListName
  );

  if (!connectorList) return {};

  let splitter: string | undefined;
  let mixer: string | undefined;

  // Connector type and name pairs
  for (let i = 1; i + 1 < connectorList.fields.length; i += 2) {
    const connectorType = connectorList.fields[i]?.value ?? '';
    const connectorName = connectorList.fields[i + 1]?.value ?? '';

    if (connectorType === 'Connector:Splitter') {
      splitter = connectorName;
    } else if (connectorType === 'Connector:Mixer') {
      mixer = connectorName;
    }
  }

  return { splitter, mixer };
}

/**
 * Detect plant loop from PlantLoop object
 */
function detectPlantLoop(
  plantLoopObj: IDFObject,
  allObjects: IDFObject[]
): DetectedPlantLoop | null {
  const idx = FIELD_INDICES.PlantLoop;
  const name = getField(plantLoopObj, idx.name);
  if (!name) return null;

  const fluidType = getField(plantLoopObj, idx.fluidType) || 'Water';
  const supplyInletNode = getField(plantLoopObj, idx.supplyInletNode);
  const supplyOutletNode = getField(plantLoopObj, idx.supplyOutletNode);
  const demandInletNode = getField(plantLoopObj, idx.demandInletNode);
  const demandOutletNode = getField(plantLoopObj, idx.demandOutletNode);
  const supplyBranchListName = getField(plantLoopObj, idx.supplyBranchListName);
  const demandBranchListName = getField(plantLoopObj, idx.demandBranchListName);
  const supplyConnectorListName = getField(plantLoopObj, idx.supplyConnectorListName);
  const demandConnectorListName = getField(plantLoopObj, idx.demandConnectorListName);

  // Parse supply side branches
  const supplyBranches: DetectedBranch[] = [];
  const supplyBranchNames = getBranchNames(supplyBranchListName, allObjects);

  for (const branchName of supplyBranchNames) {
    const branchObj = allObjects.find(
      obj => obj.className === 'Branch' && getField(obj, 0) === branchName
    );
    if (branchObj) {
      supplyBranches.push(parsePlantBranch(branchObj, allObjects, 'supply'));
    }
  }

  // Parse demand side branches
  const demandBranches: DetectedBranch[] = [];
  const demandBranchNames = getBranchNames(demandBranchListName, allObjects);

  for (const branchName of demandBranchNames) {
    const branchObj = allObjects.find(
      obj => obj.className === 'Branch' && getField(obj, 0) === branchName
    );
    if (branchObj) {
      demandBranches.push(parsePlantBranch(branchObj, allObjects, 'demand'));
    }
  }

  // Get connectors
  const supplyConnectors = getConnectors(supplyConnectorListName, allObjects);
  const demandConnectors = getConnectors(demandConnectorListName, allObjects);

  // Collect all components for loop type inference
  const allComponents = [
    ...supplyBranches.flatMap(b => b.components),
    ...demandBranches.flatMap(b => b.components),
  ];

  const loopType = inferLoopType(name, fluidType, allComponents);

  // Try to extract setpoint temperature
  let setpointTemperature: number | undefined;
  const maxTemp = parseFloat(getField(plantLoopObj, idx.maxLoopTemp));
  const minTemp = parseFloat(getField(plantLoopObj, idx.minLoopTemp));
  if (!isNaN(maxTemp) && !isNaN(minTemp)) {
    setpointTemperature = (maxTemp + minTemp) / 2;
  }

  return {
    name,
    idfObjectId: plantLoopObj.id,
    loopType,
    fluidType,
    supplyInletNode,
    supplyOutletNode,
    demandInletNode,
    demandOutletNode,
    supplyBranches,
    demandBranches,
    supplySplitter: supplyConnectors.splitter,
    supplyMixer: supplyConnectors.mixer,
    demandSplitter: demandConnectors.splitter,
    demandMixer: demandConnectors.mixer,
    setpointTemperature,
  };
}

/**
 * Build node connections from detected plant components
 */
function buildPlantNodeConnections(components: DetectedPlantComponent[]): PlantNodeConnection[] {
  const connections: PlantNodeConnection[] = [];

  // Map of node name to components using it
  const nodeUsage = new Map<string, Array<{
    component: DetectedPlantComponent;
    portId: string;
    direction: 'inlet' | 'outlet';
  }>>();

  // Build usage map
  for (const comp of components) {
    for (const [nodeName, portId] of comp.nodeConnections) {
      if (!nodeUsage.has(nodeName)) {
        nodeUsage.set(nodeName, []);
      }

      const isInlet = comp.inletNodes.includes(nodeName);
      nodeUsage.get(nodeName)?.push({
        component: comp,
        portId,
        direction: isInlet ? 'inlet' : 'outlet',
      });
    }
  }

  // Find connections (outlet -> inlet with same node name)
  for (const [nodeName, usages] of nodeUsage) {
    const outlets = usages.filter(u => u.direction === 'outlet');
    const inlets = usages.filter(u => u.direction === 'inlet');

    for (const outlet of outlets) {
      for (const inlet of inlets) {
        if (outlet.component.idfObjectId === inlet.component.idfObjectId) continue;

        connections.push({
          nodeName,
          source: {
            componentName: outlet.component.name,
            portId: outlet.portId,
            idfObjectId: outlet.component.idfObjectId,
            loopSide: outlet.component.loopSide,
          },
          target: {
            componentName: inlet.component.name,
            portId: inlet.portId,
            idfObjectId: inlet.component.idfObjectId,
            loopSide: inlet.component.loopSide,
          },
        });
      }
    }
  }

  return connections;
}

/**
 * Detect all plant loops from an IDF document
 */
export function detectPlantLoops(objects: IDFObject[]): PlantDetectionResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const plantLoops: DetectedPlantLoop[] = [];
  const standaloneComponents: DetectedPlantComponent[] = [];
  const allDetectedComponents: DetectedPlantComponent[] = [];

  // Find all PlantLoop objects
  const plantLoopObjects = objects.filter(obj => obj.className === 'PlantLoop');

  if (plantLoopObjects.length === 0) {
    warnings.push('No PlantLoop objects found in the IDF document');
  }

  // Process each plant loop
  for (const plantLoopObj of plantLoopObjects) {
    try {
      const detected = detectPlantLoop(plantLoopObj, objects);
      if (detected) {
        plantLoops.push(detected);

        // Collect all components from branches
        for (const branch of detected.supplyBranches) {
          allDetectedComponents.push(...branch.components);
        }
        for (const branch of detected.demandBranches) {
          allDetectedComponents.push(...branch.components);
        }
      }
    } catch (e) {
      const error = e instanceof Error ? e.message : String(e);
      errors.push(`Failed to detect plant loop: ${error}`);
    }
  }

  // Find standalone plant components not in any detected loop
  const processedIds = new Set(allDetectedComponents.map(c => c.idfObjectId));

  for (const obj of objects) {
    if (processedIds.has(obj.id)) continue;

    const detected = extractPlantComponent(obj);
    if (detected) {
      standaloneComponents.push(detected);
      allDetectedComponents.push(detected);
    }
  }

  // Build connections
  const connections = buildPlantNodeConnections(allDetectedComponents);

  return {
    success: errors.length === 0,
    plantLoops,
    standaloneComponents,
    connections,
    errors,
    warnings,
  };
}

/**
 * Get statistics about detected plant loops
 */
export function getPlantDetectionStats(result: PlantDetectionResult): {
  loopCount: number;
  supplyComponentCount: number;
  demandComponentCount: number;
  standaloneCount: number;
  connectionCount: number;
} {
  let supplyComponentCount = 0;
  let demandComponentCount = 0;

  for (const loop of result.plantLoops) {
    for (const branch of loop.supplyBranches) {
      supplyComponentCount += branch.components.length;
    }
    for (const branch of loop.demandBranches) {
      demandComponentCount += branch.components.length;
    }
  }

  return {
    loopCount: result.plantLoops.length,
    supplyComponentCount,
    demandComponentCount,
    standaloneCount: result.standaloneComponents.length,
    connectionCount: result.connections.length,
  };
}
